import { useState, useEffect, useRef } from "react";
import { formatContactDisplayName } from "@domaddress/shared";
import type { MailerLiteConnection, MailerLiteEnsureWebhookResult } from "@domaddress/shared";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { api } from "../lib/api";
import HelpHint from "../components/HelpHint";
import { IMPORT_FIELD_DEFS, autoMapFromHeaders, mapRowToObject } from "../lib/contactImportSheet";

type MigrateResult = {
  ok: boolean;
  moved: number;
  skippedNoVast: number;
  skippedAlreadyHasMobile: number;
  skippedNotDutchMobilePattern: number;
  totalMedewerkerRows: number;
  error?: string;
};

type MigrateDominicusResult = {
  ok: boolean;
  moved: number;
  unchanged: number;
  conflictSecondAddress: number;
  totalRows: number;
  error?: string;
};

type DuplicateCluster = {
  key: string;
  reason: "email" | "name";
  weak?: true;
  contactIds: number[];
  label: string;
};

type DuplicatesPayload = {
  contactByEmail: DuplicateCluster[];
  contactByName: DuplicateCluster[];
  usersByEmail: { email: string; userIds: number[] }[];
};

type TestEmailResponse = {
  ok: boolean;
  logs: string[];
};

type TagStaffSheetResult = {
  ok: boolean;
  label: string;
  rowsTotal: number;
  rowsSkippedNoNames: number;
  notFound: number;
  skippedNotStaff: number;
  labelsInserted: number;
  labelsAlreadyPresent: number;
  notFoundSamples: string[];
};

type ContactPreview = {
  id: number;
  first_name: string;
  last_name: string;
  tussenvoegsel: string | null;
  email: string | null;
  email_prive: string | null;
  email_werk: string | null;
  dominicus_email: string | null;
  phone: string | null;
  phone_mobile: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  entity_kind: string;
  created_at: string;
  updated_at: string;
  linked_user_email: string | null;
  groups: string[];
};

const ENTITY_KIND_NL: Record<string, string> = {
  personeel: "Personeel",
  vrijwilliger: "Vrijwilliger",
  dominicusganger: "Dominicusganger",
  extern: "Extern contact",
  leverancier: "Leverancier",
};

function entityKindLabel(kind: string) {
  const k = String(kind ?? "").trim();
  if (!k) return "—";
  return ENTITY_KIND_NL[k] ?? k;
}

function formatDate(iso: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

/** Toont één contact-kaart met de vergelijkingsinfo */
function ContactCard({
  contact,
  isKeep,
  onSelect,
}: {
  contact: ContactPreview;
  isKeep: boolean;
  onSelect: () => void;
}) {
  const rows: { label: string; value: string | null }[] = [
    { label: "E-mail (privé)", value: contact.email_prive || contact.email },
    { label: "E-mail werk", value: contact.email_werk },
    { label: "E-mail Dominicus", value: contact.dominicus_email },
    { label: "Telefoon vast", value: contact.phone },
    { label: "Telefoon mobiel", value: contact.phone_mobile },
    { label: "Adres", value: [contact.address, contact.postal_code, contact.city].filter(Boolean).join(", ") || null },
    { label: "Microsoft-account", value: contact.linked_user_email },
    { label: "Groepen", value: contact.groups.length ? contact.groups.join(", ") : null },
    { label: "Aangemaakt", value: formatDate(contact.created_at) },
    { label: "Bijgewerkt", value: formatDate(contact.updated_at) },
  ];

  return (
    <div
      onClick={onSelect}
      style={{
        flex: 1,
        minWidth: 0,
        border: isKeep ? "2px solid var(--accent, #1a56db)" : "2px solid var(--border, #e5e7eb)",
        borderRadius: 10,
        padding: "1rem",
        cursor: "pointer",
        background: isKeep ? "var(--accent-bg, #eff6ff)" : "var(--card-bg, #fff)",
        transition: "border-color .15s, background .15s",
        position: "relative",
      }}
    >
      {/* Keep/Remove badge */}
      <div style={{ position: "absolute", top: 10, right: 12, fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: isKeep ? "var(--accent, #1a56db)" : "var(--text-muted, #888)" }}>
        {isKeep ? "✓ Behouden" : "Verwijderen"}
      </div>

      {/* ID badge */}
      <div style={{ fontSize: "0.72rem", color: "var(--text-muted, #888)", marginBottom: 4 }}>
        id #{contact.id} · {entityKindLabel(contact.entity_kind)}
      </div>

      <div style={{ fontSize: "1.1rem", fontWeight: 700, marginBottom: "0.75rem", paddingRight: 80 }}>
        {formatContactDisplayName(contact) || `#${contact.id}`}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
        <tbody>
          {rows.map(
            ({ label, value }) =>
              value && (
                <tr key={label} style={{ verticalAlign: "top" }}>
                  <td style={{ color: "var(--text-muted, #888)", paddingRight: 8, whiteSpace: "nowrap", paddingBottom: 3 }}>
                    {label}
                  </td>
                  <td style={{ wordBreak: "break-word", paddingBottom: 3 }}>{value}</td>
                </tr>
              )
          )}
        </tbody>
      </table>

      {/* Radio-indicator aan de onderkant */}
      <div style={{ marginTop: "0.75rem", display: "flex", alignItems: "center", gap: 6, fontSize: "0.85rem", fontWeight: 600, color: isKeep ? "var(--accent, #1a56db)" : "var(--text-muted, #888)" }}>
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            border: `2px solid ${isKeep ? "var(--accent, #1a56db)" : "var(--border, #ccc)"}`,
            background: isKeep ? "var(--accent, #1a56db)" : "transparent",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        {isKeep ? "Deze kaart behouden" : "Klik om te behouden"}
      </div>
    </div>
  );
}

export default function BeheerToolsPage() {
  const tagSheetFileRef = useRef<HTMLInputElement>(null);

  const [result, setResult] = useState<MigrateResult | null>(null);
  const [domMigrateResult, setDomMigrateResult] = useState<MigrateDominicusResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [domMigrateBusy, setDomMigrateBusy] = useState(false);
  const [err, setErr] = useState("");

  const [removeDevBusy, setRemoveDevBusy] = useState(false);
  const [removeDevMsg, setRemoveDevMsg] = useState("");
  const [removeDevErr, setRemoveDevErr] = useState("");

  // Duplicaten
  const [duplicates, setDuplicates] = useState<DuplicatesPayload | null>(null);
  const [dupLoadErr, setDupLoadErr] = useState("");

  // Geselecteerd cluster + geladen preview-kaarten
  const [selectedCluster, setSelectedCluster] = useState<DuplicateCluster | null>(null);
  const [previews, setPreviews] = useState<ContactPreview[]>([]);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState("");

  // Welke id is "keep"
  const [keepId, setKeepId] = useState<number | null>(null);

  // Merge feedback
  const [mergeBusy, setMergeBusy] = useState(false);
  const [mergeMsg, setMergeMsg] = useState("");
  const [mergeErr, setMergeErr] = useState("");

  // Testmail SMTP
  const [testMailTo, setTestMailTo] = useState("");
  const [testMailBusy, setTestMailBusy] = useState(false);
  const [testMailLogs, setTestMailLogs] = useState<string[]>([]);
  const [testMailErr, setTestMailErr] = useState("");

  /** Label + Excel → bestaande medewerker-kaarten taggen (zelfde kolom-logica als import). */
  const [tagSheetLabel, setTagSheetLabel] = useState("");
  const [tagSheetFileName, setTagSheetFileName] = useState("");
  const [tagSheetHeaders, setTagSheetHeaders] = useState<string[]>([]);
  const [tagSheetRaw, setTagSheetRaw] = useState<unknown[][]>([]);
  const [tagSheetMappings, setTagSheetMappings] = useState<Record<string, string>>({});
  const [tagSheetErr, setTagSheetErr] = useState("");
  const [tagSheetBusy, setTagSheetBusy] = useState(false);
  const [tagSheetResult, setTagSheetResult] = useState<TagStaffSheetResult | null>(null);
  const [existingLabelsForTag, setExistingLabelsForTag] = useState<string[]>([]);
  const [mlBackfillBusy, setMlBackfillBusy] = useState(false);
  const [mlBackfillResult, setMlBackfillResult] = useState<{
    ok: number;
    fail: number;
    skipped: number;
    errors?: string[];
  } | null>(null);
  const [mlNieuwsbriefBusy, setMlNieuwsbriefBusy] = useState(false);
  const [mlNieuwsbriefResult, setMlNieuwsbriefResult] = useState<{ queued: number; group_id: string } | null>(null);
  const [mlConnections, setMlConnections] = useState<MailerLiteConnection[]>([]);
  const [mlConnBusy, setMlConnBusy] = useState(false);
  const [mlWebhookResult, setMlWebhookResult] = useState<MailerLiteEnsureWebhookResult | null>(null);
  const [mlConnActionId, setMlConnActionId] = useState<number | null>(null);

  useEffect(() => {
    void loadDuplicates();
    void loadMailerLiteConnections();
  }, []);

  useEffect(() => {
    api
      .get<{ data: string[] }>("/api/labels")
      .then((res) => setExistingLabelsForTag(res.data.data ?? []))
      .catch(() => setExistingLabelsForTag([]));
  }, []);

  async function loadDuplicates() {
    setDupLoadErr("");
    try {
      const res = await api.get<{ data: DuplicatesPayload }>("/api/beheer/duplicates");
      setDuplicates(res.data.data);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setDupLoadErr(
          "De server kent GET /api/beheer/duplicates nog niet. Deploy de nieuwste serverversie."
        );
      } else {
        const ax = e as { response?: { data?: { error?: string } } };
        setDupLoadErr(ax.response?.data?.error ?? "Dubbele-lijst niet geladen");
      }
    }
  }

  async function loadMailerLiteConnections() {
    try {
      const res = await api.get<{ data: MailerLiteConnection[] }>("/api/mailerlite/connections");
      setMlConnections(res.data.data ?? []);
    } catch {
      setMlConnections([]);
    }
  }

  async function ensureMailerLiteWebhookAdmin() {
    setMlConnBusy(true);
    setMlWebhookResult(null);
    setErr("");
    try {
      const res = await api.post<{ data: MailerLiteEnsureWebhookResult }>("/api/mailerlite/ensure-webhook");
      setMlWebhookResult(res.data.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "Webhook instellen mislukt");
    } finally {
      setMlConnBusy(false);
    }
  }

  async function pushMailerLiteConnection(id: number) {
    setMlConnActionId(id);
    setErr("");
    try {
      const res = await api.post<{ data: { examined: number; synced: number; skipped: number; failed: number } }>(
        `/api/mailerlite/connections/${id}/push`
      );
      alert(
        `Push voltooid: ${res.data.data.synced} gesynchroniseerd, ${res.data.data.skipped} overgeslagen, ${res.data.data.failed} mislukt (${res.data.data.examined} bekeken).`
      );
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "Push mislukt");
    } finally {
      setMlConnActionId(null);
    }
  }

  async function deleteMailerLiteConnection(id: number) {
    if (!confirm("Deze MailerLite-koppeling uit DomAddress verwijderen? De groep in MailerLite blijft bestaan.")) return;
    setMlConnActionId(id);
    try {
      await api.delete(`/api/mailerlite/connections/${id}`);
      await loadMailerLiteConnections();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "Verwijderen mislukt");
    } finally {
      setMlConnActionId(null);
    }
  }

  async function toggleMailerLiteConnection(conn: MailerLiteConnection) {
    setMlConnActionId(conn.id);
    try {
      await api.patch(`/api/mailerlite/connections/${conn.id}`, { enabled: !conn.enabled });
      await loadMailerLiteConnections();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "Bijwerken mislukt");
    } finally {
      setMlConnActionId(null);
    }
  }

  async function selectCluster(cluster: DuplicateCluster) {
    setSelectedCluster(cluster);
    setPreviews([]);
    setKeepId(null);
    setMergeMsg("");
    setMergeErr("");
    setPreviewErr("");
    setPreviewBusy(true);
    try {
      const res = await api.get<{ data: ContactPreview[] }>(
        `/api/beheer/contact-previews?ids=${cluster.contactIds.join(",")}`
      );
      const data = res.data.data;
      setPreviews(data);
      // Stel standaard de oudste kaart (laagste id) in als "behouden"
      if (data.length > 0) setKeepId(data[0].id);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setPreviewErr(ax.response?.data?.error ?? "Kaarten laden mislukt");
    } finally {
      setPreviewBusy(false);
    }
  }

  function clearSelection() {
    setSelectedCluster(null);
    setPreviews([]);
    setKeepId(null);
    setMergeMsg("");
    setMergeErr("");
  }

  async function runMerge() {
    if (!keepId || previews.length < 2) return;
    const removeId = previews.find((p) => p.id !== keepId)?.id;
    if (!removeId) return;
    const keepContact = previews.find((p) => p.id === keepId)!;
    const removeContact = previews.find((p) => p.id === removeId)!;
    const moreHint =
      previews.length > 2
        ? ` (Nog ${previews.length - 2} kaart${previews.length - 2 === 1 ? "" : "en"} in dit cluster: daarna opnieuw samenvoegen indien nodig.)`
        : "";
    if (
      !window.confirm(
        `"${formatContactDisplayName(removeContact)}" (#${removeId}) wordt definitief samengevoegd in "${formatContactDisplayName(keepContact)}" (#${keepId}). Deze kaart verdwijnt.${moreHint} Doorgaan?`
      )
    ) return;

    setMergeBusy(true);
    setMergeErr("");
    setMergeMsg("");
    try {
      await api.post("/api/beheer/merge-contacts", { keep_id: keepId, remove_id: removeId });
      setMergeMsg(`Samengevoegd: alles wijst nu naar kaart #${keepId} (${formatContactDisplayName(keepContact)}).`);
      clearSelection();
      await loadDuplicates();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string; detail?: string } } };
      const d = ax.response?.data;
      const msg = [d?.error, d?.detail].filter(Boolean).join(" — ") || "Samenvoegen mislukt";
      setMergeErr(msg);
    } finally {
      setMergeBusy(false);
    }
  }

  async function runTestEmail() {
    setTestMailBusy(true);
    setTestMailErr("");
    setTestMailLogs([]);
    try {
      const payload =
        testMailTo.trim().length > 0 ? { to: testMailTo.trim() } : {};
      const res = await api.post<TestEmailResponse>("/api/beheer/test-email", payload);
      setTestMailLogs(res.data.logs ?? []);
      if (!res.data.ok) {
        setTestMailErr("Verzenden mislukt of verify mislukt — zie het logvenster hieronder.");
      }
    } catch (e: unknown) {
      const ax = e as {
        response?: { status?: number; data?: { error?: string; logs?: string[] } };
      };
      setTestMailLogs(ax.response?.data?.logs ?? []);
      setTestMailErr(ax.response?.data?.error ?? "Testmail aanroep mislukt");
    } finally {
      setTestMailBusy(false);
    }
  }

  function handleTagSheetFile(ev: React.ChangeEvent<HTMLInputElement>) {
    const f = ev.target.files?.[0];
    if (!f) return;
    setTagSheetFileName(f.name);
    setTagSheetErr("");
    setTagSheetResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target?.result;
        if (typeof bstr !== "string") {
          setTagSheetErr("Bestand kon niet als tekst worden gelezen.");
          return;
        }
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1 });
        if (data.length < 2) {
          setTagSheetErr("Minimaal een kopregel en één gegevensrij nodig.");
          setTagSheetHeaders([]);
          setTagSheetRaw([]);
          return;
        }
        const firstRow = data[0].map((h) => String(h ?? "").replace(/^\uFEFF/, "").trim());
        setTagSheetHeaders(firstRow);
        setTagSheetRaw(data.slice(1) as unknown[][]);
        setTagSheetMappings(autoMapFromHeaders(firstRow, IMPORT_FIELD_DEFS));
      } catch (e: unknown) {
        setTagSheetErr(e instanceof Error ? e.message : String(e));
        setTagSheetHeaders([]);
        setTagSheetRaw([]);
      }
    };
    reader.readAsBinaryString(f);
    ev.target.value = "";
  }

  async function runTagStaffFromSheet() {
    setTagSheetErr("");
    setTagSheetResult(null);
    const label = tagSheetLabel.trim().replace(/\s+/g, " ");
    if (!label) {
      setTagSheetErr("Vul een label in.");
      return;
    }
    if (label.length > 80) {
      setTagSheetErr("Label mag maximaal 80 tekens zijn.");
      return;
    }
    if (!tagSheetMappings.first_name || !tagSheetMappings.last_name) {
      setTagSheetErr('Koppel de kolommen "Voornaam" en "Achternaam" (zie onder het bestand).');
      return;
    }
    const payload = tagSheetRaw
      .map((row) => mapRowToObject(tagSheetHeaders, tagSheetMappings, row as unknown[]))
      .filter((c) => c.first_name || c.last_name);
    if (payload.length === 0) {
      setTagSheetErr("Geen rijen met voornaam of achternaam om te verwerken.");
      return;
    }
    if (
      !window.confirm(
        `Label “${label}” toevoegen aan bestaande medewerker-kaarten (personeel/vrijwilliger) die overeenkomen met ${payload.length} rij(en) uit het bestand? Alleen kaarten die al in de app staan worden gematcht (op e-mail of op naam).`
      )
    ) {
      return;
    }
    setTagSheetBusy(true);
    try {
      const res = await api.post<TagStaffSheetResult>("/api/beheer/tag-staff-from-sheet-rows", {
        label,
        contacts: payload,
      });
      setTagSheetResult(res.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setTagSheetErr(ax.response?.data?.error ?? "Aanroep mislukt");
    } finally {
      setTagSheetBusy(false);
    }
  }

  async function runMigratePhones() {
    if (!confirm(
      "Dit verplaatst mobiele nummers (06, +316, …) van het veld 'Telefoon vast' naar 'Telefoon mobiel' voor alle medewerker-kaarten waar mobiel nog leeg is. Doorgaan?"
    )) return;
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const res = await api.post<MigrateResult>("/api/beheer/medewerkers/migrate-phones");
      setResult(res.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "Actie mislukt");
    } finally {
      setBusy(false);
    }
  }

  async function runMigrateDominicusEmails() {
    if (
      !confirm(
        "Alle contactkaarten worden doorlopen: adressen op @dominicusamsterdam.nl worden uit primair en secundair gehaald en in het veld Dominicus-e-mail gezet. Handmatig controleren daarna is slim. Doorgaan?"
      )
    ) {
      return;
    }
    setDomMigrateBusy(true);
    setErr("");
    setDomMigrateResult(null);
    try {
      const res = await api.post<MigrateDominicusResult>("/api/beheer/contacts/migrate-dominicus-emails");
      setDomMigrateResult(res.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "Actie mislukt");
    } finally {
      setDomMigrateBusy(false);
    }
  }

  async function runRemoveDevUser() {
    if (
      !confirm(
        "Hiermee wordt de gebruiker dev@domaddress.local (en eventueel OID dev-bypass-oid) uit de database verwijderd. " +
          "Kaarten die die gebruiker als aanmaker hadden, krijgen een andere gebruiker als aanmaker. " +
          "Groepsbeheerder/-kijker-rollen van dat account gaan verloren. Alleen doen als je zeker weet dat dit account nergens meer voor nodig is. Doorgaan?"
      )
    ) {
      return;
    }
    setRemoveDevBusy(true);
    setRemoveDevErr("");
    setRemoveDevMsg("");
    try {
      const res = await api.post<{
        ok: boolean;
        removed: number;
        message?: string;
        removedUserIds?: number[];
        fallbackUserId?: number;
        counts?: Record<string, number>;
        error?: string;
        detail?: string;
      }>("/api/beheer/remove-dev-user");
      const d = res.data;
      if (d.error) {
        setRemoveDevErr([d.error, d.detail].filter(Boolean).join(" — "));
        return;
      }
      const baseMsg =
        d.message ??
        (d.removed > 0 ? `Verwijderd: ${d.removed} gebruiker(s).` : "Geen dev-gebruiker gevonden.");
      const countLines =
        d.counts && Object.keys(d.counts).length > 0
          ? "\n" +
            Object.entries(d.counts)
              .filter(([, n]) => n > 0)
              .map(([k, n]) => `${k}: ${n}`)
              .join("\n")
          : "";
      setRemoveDevMsg(baseMsg + countLines);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string; detail?: string } } };
      const d = ax.response?.data;
      setRemoveDevErr([d?.error, d?.detail].filter(Boolean).join(" — ") || "Actie mislukt");
    } finally {
      setRemoveDevBusy(false);
    }
  }

  async function runMailerLiteBackfill() {
    if (
      !confirm(
        "Alle contactkaarten worden naar MailerLite gesynchroniseerd (kan even duren; let op API-limiet). Zet eerst MAILERLITE_API_TOKEN en groeps-IDs in de server-omgeving. Doorgaan?"
      )
    ) {
      return;
    }
    setMlBackfillBusy(true);
    setErr("");
    setMlBackfillResult(null);
    try {
      const res = await api.post<{ data: { ok: number; fail: number; skipped: number; errors: string[] } }>(
        "/api/mailerlite/backfill"
      );
      setMlBackfillResult(res.data.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "MailerLite-backfill mislukt");
    } finally {
      setMlBackfillBusy(false);
    }
  }

  async function runNieuwsbriefBackfill() {
    if (
      !confirm(
        "Alle dominicusgangers die de nieuwsbrief willen, worden in de MailerLite-groep Nieuwsbrief gezet. Dit is een eenmalige actie. Doorgaan?"
      )
    ) {
      return;
    }
    setMlNieuwsbriefBusy(true);
    setErr("");
    setMlNieuwsbriefResult(null);
    try {
      const res = await api.post<{ data: { queued: number; group_id: string } }>("/api/mailerlite/nieuwsbrief-backfill");
      setMlNieuwsbriefResult(res.data.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error ?? "Nieuwsbrief-synchronisatie mislukt");
    } finally {
      setMlNieuwsbriefBusy(false);
    }
  }

  const allClusters: (DuplicateCluster & { strong: boolean })[] = [
    ...(duplicates?.contactByEmail ?? []).map((c) => ({ ...c, strong: true })),
    ...(duplicates?.contactByName ?? []).map((c) => ({ ...c, strong: false })),
  ];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link to="/medewerkers" className="btn-back" style={{ display: "inline-block", marginBottom: "0.5rem" }}>
            ← App
          </Link>
          <div className="page-title-row" style={{ marginBottom: 2 }}>
            <h2 className="page-title" style={{ margin: 0 }}>
              Beheer: scripts
            </h2>
            <HelpHint label="Uitleg: beheerscripts">
              Eenmalig onderhoud. Elke actie schrijft ook een regel in het <strong>activiteitenlog</strong>.
            </HelpHint>
          </div>
        </div>
      </div>

      {err && <p className="form-error" style={{ marginBottom: "1rem" }}>{err}</p>}

      {/* ── Eénmalig: dev@domaddress.local uit productie-database ── */}
      <div className="detail-section" style={{ maxWidth: 760, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">Database: dev-account verwijderen</h3>
          <HelpHint label="Uitleg: dev-account">
            Verwijdert de rij(en) met e-mail <code>dev@domaddress.local</code> of Microsoft-OID{" "}
            <code>dev-bypass-oid</code> uit de tabel <strong>users</strong>. Alle kaarten en andere records die die
            gebruiker als <em>aanmaker</em> hadden, krijgen automatisch een andere gebruiker (voorkeur: super-admin met
            laagste id). Groeps-rollen van het dev-account (beheerder/kijker) worden gewist; voorzitter in{" "}
            <code>group_chairs</code> wordt waar nodig overgezet. Er wordt een regel in het activiteitenlog geschreven.
          </HelpHint>
        </div>
        <p className="page-subtitle" style={{ marginTop: 0 }}>
          Eénmalige opruiming na ontwikkeling. Alleen uitvoeren op productie als je zeker weet dat er nog minstens één
          echte Microsoft-gebruiker in de database staat.
        </p>
        <button type="button" className="btn-primary" onClick={() => void runRemoveDevUser()} disabled={removeDevBusy}>
          {removeDevBusy ? "Bezig…" : "Verwijder dev@domaddress.local uit database"}
        </button>
        {removeDevErr && <p className="form-error" style={{ marginTop: "0.75rem" }}>{removeDevErr}</p>}
        {removeDevMsg && (
          <pre
            style={{
              marginTop: "0.75rem",
              padding: "0.75rem 1rem",
              fontSize: "0.85rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "var(--code-bg, #f4f4f5)",
              border: "1px solid var(--border, #e5e7eb)",
              borderRadius: 8,
            }}
          >
            {removeDevMsg}
          </pre>
        )}
      </div>

      {/* ── Testmail SMTP ── */}
      <div className="detail-section" style={{ maxWidth: 760, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">E-mail: test verzenden</h3>
          <HelpHint label="Uitleg: testmail">
            Controleert SMTP-instellingen (<code>SMTP_*</code> in de omgeving): eerst <strong>verify</strong>, daarna
            een korte testmail. Het logvenster toont elke stap. Standaard gaat de mail naar het e-mailadres van je
            account; je kunt optioneel een ander geldig adres invullen.
          </HelpHint>
        </div>
        <p className="page-subtitle" style={{ marginTop: 0 }}>
          Leeg laten = versturen naar je login-e-mail. Zie je inbox (en spam) na een geslaagde run.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", alignItems: "center", marginBottom: "0.75rem" }}>
          <input
            type="email"
            className="form-input"
            placeholder="Optioneel: ander ontvanger-adres"
            value={testMailTo}
            onChange={(ev) => setTestMailTo(ev.target.value)}
            style={{ minWidth: 260, flex: "1 1 200px" }}
            disabled={testMailBusy}
            autoComplete="email"
          />
          <button type="button" className="btn-primary" onClick={() => void runTestEmail()} disabled={testMailBusy}>
            {testMailBusy ? "Bezig…" : "Testmail versturen"}
          </button>
        </div>
        {testMailErr && <p className="form-error" style={{ marginBottom: "0.5rem" }}>{testMailErr}</p>}
        {(testMailLogs.length > 0 || testMailBusy) && (
          <div>
            <p className="page-subtitle" style={{ marginBottom: 6, fontWeight: 600 }}>
              Log
            </p>
            <pre
              style={{
                margin: 0,
                padding: "0.75rem 1rem",
                maxHeight: 280,
                overflow: "auto",
                fontSize: "0.8rem",
                lineHeight: 1.45,
                background: "var(--code-bg, #f4f4f5)",
                border: "1px solid var(--border, #e5e7eb)",
                borderRadius: 8,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {testMailBusy && testMailLogs.length === 0 ? "Bezig…\n" : testMailLogs.join("\n")}
            </pre>
          </div>
        )}
      </div>

      {/* ── Label vanuit Excel op bestaande medewerkers ── */}
      <div className="detail-section" style={{ maxWidth: 760, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">Label vanuit Excel (bestaande medewerkers)</h3>
          <HelpHint label="Uitleg: label vanuit sheet">
            Kies een <strong>label</strong> en hetzelfde soort <strong>Excel/CSV</strong> als bij import (eerste regel =
            kolomnamen). De app leest elke rij en zoekt een bestaande kaart in <strong>personeel</strong> of{" "}
            <strong>vrijwilliger</strong>. Match: eerst op <strong>privé-e-mail</strong> (als de cel een geldig adres is),
            anders op <strong>voornaam + achternaam</strong> — net als bij het herkennen van doublures bij import. Alleen
            wie al zo in de database staat, krijgt het label; rijen zonder treffer tellen bij “niet gevonden”.
          </HelpHint>
        </div>
        <p className="page-subtitle" style={{ marginTop: 0 }}>
          Handig om naderhand iedereen uit een bepaald bestand dezelfde vrije tag te geven, zonder opnieuw te importeren.
        </p>

        <div className="form-group" style={{ marginBottom: "1rem" }}>
          <label htmlFor="beheer-tag-sheet-label">Label (vrije tag)</label>
          <input
            id="beheer-tag-sheet-label"
            type="text"
            className="form-input"
            style={{ maxWidth: "100%", width: "100%" }}
            list="beheer-tag-sheet-label-datalist"
            value={tagSheetLabel}
            onChange={(e) => setTagSheetLabel(e.target.value)}
            placeholder="bijv. team_foto_2025"
            maxLength={80}
            disabled={tagSheetBusy}
            autoComplete="off"
          />
          <datalist id="beheer-tag-sheet-label-datalist">
            {existingLabelsForTag.map((lbl) => (
              <option key={lbl} value={lbl} />
            ))}
          </datalist>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <input
            ref={tagSheetFileRef}
            type="file"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            style={{ display: "none" }}
            onChange={handleTagSheetFile}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => tagSheetFileRef.current?.click()}
            disabled={tagSheetBusy}
          >
            Excel/CSV kiezen
          </button>
          {tagSheetFileName && (
            <span className="page-subtitle" style={{ marginLeft: 12 }}>
              {tagSheetFileName} — {tagSheetRaw.length} gegevensrijen
            </span>
          )}
        </div>

        {tagSheetHeaders.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <p style={{ fontWeight: 600, marginBottom: "0.75rem" }}>Kolommen koppelen (zoals bij import)</p>
            <div style={{ display: "grid", gap: "0.65rem", maxHeight: 320, overflow: "auto", paddingRight: 4 }}>
              {IMPORT_FIELD_DEFS.map((field) => (
                <div
                  key={field.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    padding: "0.65rem 0.85rem",
                    background: "var(--surface, #f8fafc)",
                    borderRadius: 6,
                    border: "1px solid var(--border, #e5e7eb)",
                  }}
                >
                  <span style={{ fontSize: "0.9rem" }}>
                    <strong>{field.label}</strong>
                    {field.required && (
                      <span style={{ fontSize: "0.75rem", color: "var(--text-muted, #888)" }}> (verplicht)</span>
                    )}
                  </span>
                  <select
                    style={{ minWidth: 220, maxWidth: "100%", padding: "0.45rem", borderRadius: 4, border: "1px solid var(--border)" }}
                    value={tagSheetMappings[field.key] || ""}
                    onChange={(e) => setTagSheetMappings({ ...tagSheetMappings, [field.key]: e.target.value })}
                    disabled={tagSheetBusy}
                    aria-label={`Koppeling voor ${field.label}`}
                  >
                    <option value="">— Negeren —</option>
                    {tagSheetHeaders.map((h, i) => (
                      <option key={i} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {tagSheetErr && <p className="form-error" style={{ marginBottom: "0.75rem" }}>{tagSheetErr}</p>}

        <button
          type="button"
          className="btn-primary"
          onClick={() => void runTagStaffFromSheet()}
          disabled={tagSheetBusy || tagSheetRaw.length === 0}
        >
          {tagSheetBusy ? "Bezig…" : "Label op matchende medewerkers zetten"}
        </button>

        {tagSheetResult?.ok && (
          <ul className="page-subtitle" style={{ marginTop: "1rem", listStyle: "disc", paddingLeft: "1.25rem" }}>
            <li>
              Label: <strong>{tagSheetResult.label}</strong>
            </li>
            <li>Rijen in bestand: {tagSheetResult.rowsTotal}</li>
            <li>Nieuw label gezet: <strong>{tagSheetResult.labelsInserted}</strong></li>
            <li>Hadden dit label al: {tagSheetResult.labelsAlreadyPresent}</li>
            <li>Zonder voornaam/achternaam overgeslagen: {tagSheetResult.rowsSkippedNoNames}</li>
            <li>Geen matchende medewerker-kaart: {tagSheetResult.notFound}</li>
            {tagSheetResult.skippedNotStaff > 0 && <li>Niet-personeel/vrijwilliger overgeslagen: {tagSheetResult.skippedNotStaff}</li>}
          </ul>
        )}
        {tagSheetResult?.ok && tagSheetResult.notFoundSamples.length > 0 && (
          <p className="page-subtitle" style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "var(--text-muted, #666)" }}>
            <strong>Voorbeelden niet gevonden:</strong> {tagSheetResult.notFoundSamples.join(" · ")}
          </p>
        )}
      </div>

      {/* ── Dubbele kaarten & samenvoegen ── */}
      <div className="detail-section" style={{ maxWidth: 760, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">Dubbele contactkaarten & samenvoegen</h3>
          <HelpHint label="Uitleg: dubbele kaarten">
            Zoekt in <strong>alle</strong> contacten (personeel, vrijwilliger, dominicusganger, extern, leverancier).
            Dezelfde <strong>e-mail</strong> op meerdere kaarten is meestal een echte duplicaat. Zelfde{" "}
            <strong>voor- en achternaam</strong> zonder e-mail is een <em>tip</em> — controleer even. Klik op een
            cluster om de kaarten te vergelijken en samen te voegen; de kaart die je behoudt bepaalt o.a. de primaire
            lijst (personeel, vrijwilliger, …). Bij <strong>drie of meer</strong> dubbele kaarten in één cluster: telkens
            één paar samenvoegen tot er één over is.{" "}
            <strong>Geen</strong> samenvoeging als beide kaarten al een Microsoft-account hebben: los dat eerst op in
            Gebruikers &amp; app-rollen.
          </HelpHint>
        </div>

        {dupLoadErr && <p className="form-error">{dupLoadErr}</p>}
        {mergeMsg && (
          <p style={{ color: "var(--ok, #1a7f37)", marginBottom: "1rem", fontWeight: 500 }}>
            ✓ {mergeMsg}
          </p>
        )}

        {/* Cluster-lijst */}
        {duplicates && !selectedCluster && (
          <>
            {allClusters.length === 0 ? (
              <p className="page-subtitle" style={{ marginTop: 0 }}>Geen dubbele contactkaarten gevonden.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: "1rem" }}>
                {allClusters.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => void selectCluster(c)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "0.55rem 0.85rem",
                      border: "1px solid var(--border, #e5e7eb)",
                      borderRadius: 8,
                      background: "var(--card-bg, #fff)",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: "0.9rem",
                    }}
                  >
                    <span style={{
                      flexShrink: 0,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      padding: "2px 6px",
                      borderRadius: 4,
                      background: c.strong ? "var(--accent-bg, #eff6ff)" : "var(--badge-bg, #f3f4f6)",
                      color: c.strong ? "var(--accent, #1a56db)" : "var(--text-muted, #888)",
                    }}>
                      {c.strong ? "e-mail" : "naam"}
                    </span>
                    <span style={{ fontWeight: 600 }}>{c.label}</span>
                    <span style={{ color: "var(--text-muted, #888)", fontSize: "0.8rem" }}>
                      id's: {c.contactIds.join(", ")}
                    </span>
                    <span style={{ marginLeft: "auto", color: "var(--text-muted, #888)", fontSize: "0.85rem" }}>
                      Vergelijken →
                    </span>
                  </button>
                ))}
              </div>
            )}

            {duplicates.usersByEmail.length > 0 && (
              <p className="page-subtitle" style={{ color: "var(--text-muted, #666)", marginTop: "0.5rem" }}>
                <strong>Let op (users-tabel, zelfde e-mail — handmatig oplossen):</strong>{" "}
                {duplicates.usersByEmail.map((u) => (
                  <span key={u.email} style={{ display: "block", marginTop: 4 }}>
                    {u.email} — user id's: {u.userIds.join(", ")}
                  </span>
                ))}
              </p>
            )}
          </>
        )}

        {/* Vergelijkings-kaarten */}
        {selectedCluster && (
          <div>
            <button
              type="button"
              onClick={clearSelection}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent, #1a56db)", fontSize: "0.9rem", padding: 0, marginBottom: "1rem" }}
            >
              ← Terug naar lijst
            </button>

            {previewBusy && <p className="page-subtitle">Kaarten laden…</p>}
            {previewErr && <p className="form-error">{previewErr}</p>}

            {previews.length >= 2 && (
              <>
                <p className="page-subtitle" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                  Klik op de kaart die je wilt <strong>behouden</strong>. De andere wordt verwijderd (lege velden worden
                  overgenomen).
                </p>
                <div style={{ display: "flex", gap: "1rem", alignItems: "stretch", flexWrap: "wrap" }}>
                  {previews.map((contact) => (
                    <ContactCard
                      key={contact.id}
                      contact={contact}
                      isKeep={keepId === contact.id}
                      onSelect={() => setKeepId(contact.id)}
                    />
                  ))}
                </div>

                {mergeErr && <p className="form-error" style={{ marginTop: "0.75rem" }}>{mergeErr}</p>}

                <div style={{ marginTop: "1rem", display: "flex", gap: "0.75rem", alignItems: "center" }}>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void runMerge()}
                    disabled={mergeBusy || !keepId}
                  >
                    {mergeBusy ? "Bezig…" : "Samenvoegen"}
                  </button>
                  <span style={{ fontSize: "0.85rem", color: "var(--text-muted, #888)" }}>
                    {keepId
                      ? `Behoudt #${keepId}, verwijdert: ${previews
                          .filter((p) => p.id !== keepId)
                          .map((p) => `#${p.id}`)
                          .join(", ")}`
                      : "Selecteer een kaart om te behouden"}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Dominicus-e-mail migratie ── */}
      <div className="detail-section" style={{ maxWidth: 640, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">E-mail: Dominicus-adressen verplaatsen</h3>
          <HelpHint label="Uitleg: Dominicus-e-mail">
            Loopt over <strong>alle</strong> contactkaarten. Staat in primair of secundair een adres op{" "}
            <strong>@dominicusamsterdam.nl</strong>, dan wordt dat verplaatst naar het veld{" "}
            <strong>Dominicus-e-mail</strong> en gewist bij primair/secundair. Als er al een ander Dominicus-adres in
            dat veld stond en primair een <em>tweede</em> organisatie-adres is, wordt die regel overgeslagen (teller
            “tweede adres”) — los dat handmatig op.
          </HelpHint>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void runMigrateDominicusEmails()}
          disabled={domMigrateBusy}
        >
          {domMigrateBusy ? "Bezig…" : "Script uitvoeren"}
        </button>
        {domMigrateResult?.ok && (
          <ul className="page-subtitle" style={{ marginTop: "1rem", listStyle: "disc", paddingLeft: "1.25rem" }}>
            <li>Kaarten bijgewerkt: <strong>{domMigrateResult.moved}</strong></li>
            <li>Geen wijziging nodig: {domMigrateResult.unchanged}</li>
            <li>Tweede organisatie-adres (handmatig oplossen): {domMigrateResult.conflictSecondAddress}</li>
            <li>Kaarten in totaal: {domMigrateResult.totalRows}</li>
          </ul>
        )}
      </div>

      {/* ── MailerLite backfill ── */}
      <div className="detail-section" style={{ maxWidth: 640, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">MailerLite: alle kaarten synchroniseren</h3>
          <HelpHint label="Uitleg: MailerLite-backfill">
            Roept voor elke contactkaart dezelfde sync aan als <strong>Sync naar MailerLite</strong> op een kaart.
            Vereist geldige <code>MAILERLITE_API_TOKEN</code> en groeps-ID&apos;s in de omgeving.
          </HelpHint>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void runMailerLiteBackfill()}
          disabled={mlBackfillBusy}
        >
          {mlBackfillBusy ? "Bezig…" : "Backfill uitvoeren"}
        </button>
        {mlBackfillResult != null && (
          <ul className="page-subtitle" style={{ marginTop: "1rem", listStyle: "disc", paddingLeft: "1.25rem" }}>
            <li>Gelukt: <strong>{mlBackfillResult.ok}</strong></li>
            <li>Overgeslagen: {mlBackfillResult.skipped}</li>
            <li>Mislukt: {mlBackfillResult.fail}</li>
            {mlBackfillResult.errors?.length ? (
              <li>
                Fouten (max. 50):{" "}
                <code style={{ fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>{mlBackfillResult.errors.join("; ")}</code>
              </li>
            ) : null}
          </ul>
        )}
      </div>

      <div className="detail-section" style={{ maxWidth: 640, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">MailerLite: nieuwsbrief voor dominicusgangers</h3>
          <HelpHint label="Uitleg: nieuwsbrief-koppeling">
            Eenmalig. Zet elke dominicusganger met het vinkje Nieuwsbrief in de MailerLite-groep{" "}
            <strong>Nieuwsbrief</strong>. Nieuwe imports doen dit daarna vanzelf. Wie in MailerLite uit die groep gaat,
            verliest het vinkje in DomAddress.
          </HelpHint>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void runNieuwsbriefBackfill()}
          disabled={mlNieuwsbriefBusy}
        >
          {mlNieuwsbriefBusy ? "Bezig…" : "Bestaande dominicusgangers naar Nieuwsbrief"}
        </button>
        {mlNieuwsbriefResult != null && (
          <p className="page-subtitle" style={{ marginTop: "1rem" }}>
            In de wachtrij: <strong>{mlNieuwsbriefResult.queued}</strong>. De server stuurt ze één voor één naar
            MailerLite.
          </p>
        )}
      </div>

      {/* ── MailerLite: koppelingen beheren ── */}
      <div className="detail-section" style={{ maxWidth: 900, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">MailerLite: koppelingen</h3>
          <HelpHint label="Uitleg: MailerLite-koppelingen">
            Overzicht van alle groep-koppelingen (e-maillijsten en adresboeklijsten). Groep-IDs staan in de database;
            <code>MAILERLITE_GROUP_*</code> in .env is alleen een eenmalige seed. Gebruik &quot;Webhook controleren&quot;
            om de account-webhook automatisch aan te maken (vereist <code>BASE_URL</code> op de server).
          </HelpHint>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <button type="button" className="btn-secondary" disabled={mlConnBusy} onClick={() => void ensureMailerLiteWebhookAdmin()}>
            {mlConnBusy ? "Bezig…" : "Webhook controleren / aanmaken"}
          </button>
          <button type="button" className="btn-secondary" onClick={() => void loadMailerLiteConnections()}>
            Vernieuwen
          </button>
        </div>
        {mlWebhookResult && (
          <p className="page-subtitle" style={{ marginBottom: "1rem" }}>
            {mlWebhookResult.message}
            {mlWebhookResult.webhook_id ? ` (id: ${mlWebhookResult.webhook_id})` : ""}
            {mlWebhookResult.secret_stored ? " — geheim opgeslagen in database." : ""}
          </p>
        )}
        {mlConnections.length === 0 ? (
          <p className="page-subtitle">Nog geen koppelingen. Maak er een aan via een lijstpagina (knop &quot;MailerLite koppelen&quot;).</p>
        ) : (
          <table className="data-table" style={{ width: "100%", fontSize: "0.9rem" }}>
            <thead>
              <tr>
                <th>MailerLite-groep</th>
                <th>Bron</th>
                <th>Label</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {mlConnections.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.ml_group_name}</strong>
                    <br />
                    <code style={{ fontSize: "0.75rem" }}>{c.ml_group_id}</code>
                  </td>
                  <td>
                    {c.source_type}
                    {c.source_key ? ` / ${c.source_key}` : ""}
                  </td>
                  <td>{c.membership_label ?? "—"}</td>
                  <td>{c.enabled ? (c.two_way ? "actief, 2-weg" : "actief") : "uit"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="btn-link"
                      disabled={mlConnActionId === c.id}
                      onClick={() => void pushMailerLiteConnection(c.id)}
                    >
                      Push
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="btn-link"
                      disabled={mlConnActionId === c.id}
                      onClick={() => void toggleMailerLiteConnection(c)}
                    >
                      {c.enabled ? "Uit" : "Aan"}
                    </button>
                    {" · "}
                    <button
                      type="button"
                      className="btn-link"
                      disabled={mlConnActionId === c.id}
                      onClick={() => void deleteMailerLiteConnection(c.id)}
                    >
                      Verwijder
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── MailerLite: lijsten vanuit MailerLite ── */}
      <div className="detail-section" style={{ maxWidth: 640, marginBottom: "2rem" }}>
        <div className="section-title-row">
          <h3 className="section-title">MailerLite: e-maillijsten importeren (MailerLite leidend)</h3>
          <HelpHint label="Uitleg: lijsten uit MailerLite">
            Haalt alle abonnees op uit MailerLite en toont ze gegroepeerd: al gekoppeld (bijwerken), al gelijk
            (geen actie), en nog niet gevonden. Voor onbekende abonnees kun je een bestaande kaart zoeken om te
            koppelen, of een nieuwe dominicusganger-kaart aanmaken.
          </HelpHint>
        </div>
        <Link to="/admin/mailerlite-import" className="btn-primary" style={{ display: "inline-block" }}>
          Openen: abonnees bekijken en koppelen →
        </Link>
      </div>

      {/* ── Telefoon-script ── */}
      <div className="detail-section" style={{ maxWidth: 640 }}>
        <div className="section-title-row">
          <h3 className="section-title">Telefoon: mobiel uit &apos;vast&apos; veld</h3>
          <HelpHint label="Uitleg: script telefoonvelden">
            Loopt over alle <strong>medewerker</strong>-kaarten. Als in <strong>Telefoon vast</strong> een duidelijk
            Nederlands mobiel nummer staat (06, +316, +3106, 00316, …) en <strong>Telefoon mobiel</strong> nog leeg
            is, zet het script dat nummer op mobiel en wist het bij vast.
          </HelpHint>
        </div>
        <button type="button" className="btn-primary" onClick={() => void runMigratePhones()} disabled={busy}>
          {busy ? "Bezig…" : "Script uitvoeren"}
        </button>
        {result?.ok && (
          <ul className="page-subtitle" style={{ marginTop: "1rem", listStyle: "disc", paddingLeft: "1.25rem" }}>
            <li>Aangepast: <strong>{result.moved}</strong></li>
            <li>Geen vast nummer: {result.skippedNoVast}</li>
            <li>Had al een mobiel nummer: {result.skippedAlreadyHasMobile}</li>
            <li>Geen herkenning als NL-mobiel: {result.skippedNotDutchMobilePattern}</li>
            <li>Medewerker-kaarten in totaal: {result.totalMedewerkerRows}</li>
          </ul>
        )}
      </div>
    </div>
  );
}
